import React, { useEffect, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextInput,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const emptyForm = {
    name: '',
    fromAddress: '',
    host: '',
    username: '',
    password: '',
    ignoreCertErrors: true,
};

function AwarePhishSendingProfiles() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const { sendingProfiles, loadingProfiles, savingAsset } = awarePhishStore;

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (companyId) awarePhishStore.loadSendingProfiles(companyId);
    }, [companyId]);

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowForm(true);
    };

    const startEdit = (p) => {
        setEditingId(p.id);
        setForm({
            name: p.name || '',
            fromAddress: p.fromAddress || '',
            host: p.host || '',
            username: p.username || '',
            password: '',
            ignoreCertErrors: p.ignoreCertErrors !== false,
        });
        setShowForm(true);
    };

    const save = async () => {
        try {
            const payload = { ...form };
            if (editingId && !payload.password) {
                delete payload.password;
            }
            await awarePhishStore.saveSendingProfile(
                companyId,
                editingId,
                payload
            );
            commonStore.success(
                editingId ? 'Sending profile updated' : 'Sending profile created'
            );
            setShowForm(false);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save sending profile'
            );
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete sending profile “${p.name}”?`)) return;
        try {
            await awarePhishStore.removeSendingProfile(companyId, p.id);
            commonStore.success('Sending profile deleted');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to delete sending profile'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH sending profiles</title>
            </Helmet>
            <HeaderSection
                heading="Sending profiles"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Per-company SMTP for phishing mail. Use a dedicated domain (e.g. Mailgun), not marketing. Host: smtp.mailgun.org:587'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New profile"
                        mode="primary"
                        onClick={startCreate}
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="sending-profiles" />

            {showForm ? (
                <Block padding="20" column style={{ marginBottom: 14 }}>
                    <Heading3>
                        {editingId ? 'Edit profile' : 'New profile'}
                    </Heading3>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.name}
                        onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                        }
                        placeholder="e.g. Mailgun phishing"
                    />
                    <FieldLabel>From address</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.fromAddress}
                        onChange={(e) =>
                            setForm({ ...form, fromAddress: e.target.value })
                        }
                        placeholder="security@phish.example.com"
                    />
                    <FieldLabel>SMTP host</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.host}
                        onChange={(e) =>
                            setForm({ ...form, host: e.target.value })
                        }
                        placeholder="smtp.mailgun.org:587"
                    />
                    <FieldLabel>Username</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.username}
                        onChange={(e) =>
                            setForm({ ...form, username: e.target.value })
                        }
                    />
                    <FieldLabel>
                        Password
                        {editingId ? ' (leave blank to keep current)' : ''}
                    </FieldLabel>
                    <TextInput
                        fullWidth
                        type="password"
                        value={form.password}
                        onChange={(e) =>
                            setForm({ ...form, password: e.target.value })
                        }
                    />
                    <label
                        style={{
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            marginTop: 12,
                            fontSize: 13,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={form.ignoreCertErrors}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    ignoreCertErrors: e.target.checked,
                                })
                            }
                        />
                        Ignore certificate errors
                    </label>
                    <FlexDiv gap={12} style={{ marginTop: 16 }}>
                        <ThemedButton
                            text={savingAsset ? 'Saving…' : 'Save'}
                            mode="primary"
                            onClick={save}
                            disabled={savingAsset}
                            $noWrap
                        />
                        <ThemedButton
                            text="Cancel"
                            mode="secondary"
                            onClick={() => setShowForm(false)}
                            $noWrap
                        />
                    </FlexDiv>
                </Block>
            ) : null}

            <Block tablePadding column>
                {loadingProfiles && !(sendingProfiles || []).length ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !(sendingProfiles || []).length ? (
                    <TextDefault lighter>
                        No sending profiles yet — create one before launching a
                        simulation.
                    </TextDefault>
                ) : (
                    (sendingProfiles || []).map((p) => (
                        <FlexDiv
                            key={p.id}
                            justifyBetween
                            alignCenter
                            style={{
                                padding: '12px 0',
                                borderBottom: `1px solid ${palette.graphite.silver}`,
                            }}
                        >
                            <div>
                                <TextDefault bold>{p.name}</TextDefault>
                                <TextTiny lighter>
                                    {p.fromAddress} · {p.host}
                                </TextTiny>
                            </div>
                            <FlexDiv gap={8}>
                                <ThemedButton
                                    text="Edit"
                                    mode="secondary"
                                    onClick={() => startEdit(p)}
                                    $noWrap
                                />
                                <ThemedButton
                                    text="Delete"
                                    mode="secondary"
                                    onClick={() => remove(p)}
                                    $noWrap
                                />
                            </FlexDiv>
                        </FlexDiv>
                    ))
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishSendingProfiles);
